/* eslint-disable camelcase, max-lines */
import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';
import qs from 'qs';
import { isMobile } from 'react-device-detect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import { ENUM_FAVOURITE_STATUS } from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import ApiService from 'services/ApiService/ApiService';
import {
  getApiWarnMsg,
  getDomain,
  isValidResponse,
} from 'utils/helpers';
import makeAction from 'utils/makeAction';
import parseCatch from 'utils/parseCatch';

import {
  FANCLUB_STATUS,
  FAN_CLUB_PRODUCT_URL,
  FAVOURITES_BASE,
  FAVOURITES_COUNT,
  FRIENDS_STATUS_URL,
  GET_BROADCAST_SOURCE_URL,
  GET_FANCLUB_AUTHORIZATION_URL,
  GET_FANCLUB_INFO_URL,
  GET_PERFORMER_DETAILED_INFO_URL,
  GET_PERFORMER_GALLERIES_URL,
  GET_PERFORMER_GALLERY_IMAGES_URL,
  GET_PERFORMER_GIFTS_URL,
  GET_PERFORMER_INFO_URL,
  GET_PERFORMER_VIDEOS_URL,
  GET_PERFORMER_WISHLIST_URL,
  GET_SERVER_TIME_URL,
  PAY_SUBSCRIPTION_URL,
  PRIVATE_SHOW_ACTION_URL,
} from './Api';


const GET_PERFORMER_DETAILED_INFO = 'cam4/profile/GET_PERFORMER_DETAILED_INFO';
const GET_PERFORMER_FRIENDS_STATUS = 'cam4/profile/GET_PERFORMER_FRIENDS_STATUS';
const GET_PERFORMER_GALLERIES = 'cam4/profile/GET_PERFORMER_GALLERIES';
const GET_PERFORMER_GIFTS = 'cam4/profile/GET_PERFORMER_GIFTS';
const GET_PERFORMER_INFO = 'cam4/profile/GET_PERFORMER_INFO';
const GET_PERFORMER_VIDEOS = 'cam4/profile/GET_PERFORMER_VIDEOS';
const GET_PERFORMER_WISHLIST = 'cam4/profile/GET_PERFORMER_WISHLIST';
const GET_SERVER_TIME = 'cam4/profile/GET_SERVER_TIME';
const GET_STREAM_URL = 'cam4/profile/GET_STREAM_URL';
const SET_FAN_CLUB_PROCESSOR_ID = 'cam4/profile/SET_FAN_CLUB_PROCESSOR_ID';
const SET_FAN_CLUB_PRODUCT_ID = 'cam4/profile/SET_FAN_CLUB_PRODUCT_ID';
const SET_FAN_CLUB_TOKEN_PRICE = 'cam4/profile/SET_FAN_CLUB_TOKEN_PRICE';
const SET_HAS_FAN_CLUB = 'cam4/profile/SET_HAS_FAN_CLUB';
const SET_IS_FAN = 'cam4/profile/SET_IS_FAN';
const SET_PERFORMER_FANCLUB_STATUS = 'cam4/profile/SET_PERFORMER_FANCLUB_STATUS';
const SET_PERFORMER_FAVOURITES_COUNT = 'cam4/profile/SET_PERFORMER_FAVOURITES_COUNT';
const SET_PERFORMER_FAVOURITES_STATUS = 'cam4/profile/SET_PERFORMER_FAVOURITES_STATUS';
const SET_PLAY_TOKEN = 'cam4/profile/SET_PLAY_TOKEN';

const initialState = Immutable.fromJS({
  broadcastSource: [],
  fanClubProductId: 0,
  hasFanClub: null,
  isFan: null,
  performerDetailedInfo: null,
  performerFanclubStatus: {},
  performerFavourites: {
    count: '-',
    status: ENUM_FAVOURITE_STATUS.NOT_FAVORITE,
  },
  performerFriendsStatus: null,
  performerGalleries: [],
  performerGifts: null,
  performerInfo: null,
  performerWishlist: null,
  playToken: null,
  processorId: 0,
  serverTime: null,
  tokenPrice: 0,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GET_SERVER_TIME:
      return state.set('serverTime', action.serverTime);

    case GET_PERFORMER_INFO:
      return state.set('performerInfo', action.performerInfo);

    case GET_PERFORMER_VIDEOS:
      return state.set('performerVideos', action.performerVideos);

    case GET_PERFORMER_DETAILED_INFO:
      return state.set('performerDetailedInfo', action.performerDetailedInfo);

    case GET_PERFORMER_GALLERIES:
      return state.set('performerGalleries', action.performerGalleries);

    case GET_PERFORMER_GIFTS:
      return state.set('performerGifts', action.performerGifts);

    case GET_PERFORMER_FRIENDS_STATUS:
      return state.set('performerFriendsStatus', action.performerFriendsStatus);

    case SET_PERFORMER_FAVOURITES_COUNT:
      return state.setIn(['performerFavourites', 'count'], action.favoritesCount);

    case SET_PERFORMER_FAVOURITES_STATUS:
      return state.setIn(['performerFavourites', 'status'], action.favoritesStatus);

    case GET_STREAM_URL:
      return state.set('broadcastSource', action.broadcastSource);

    case SET_PLAY_TOKEN:
      return state.set('playToken', action.playToken);

    case SET_IS_FAN:
      return state.set('isFan', action.isFan);

    case SET_HAS_FAN_CLUB:
      return state.set('hasFanClub', action.hasFanClub);

    case SET_FAN_CLUB_TOKEN_PRICE:
      return state.set('tokenPrice', action.tokenPrice);

    case SET_FAN_CLUB_PRODUCT_ID:
      return state.set('fanClubProductId', action.fanClubProductId);

    case SET_FAN_CLUB_PROCESSOR_ID:
      return state.set('processorId', action.fanClubProcessorId);

    case GET_PERFORMER_WISHLIST:
      return state.set('performerWishlist', action.performerWishlist);

    case SET_PERFORMER_FANCLUB_STATUS:
      return state.set('performerFanclubStatus', action.performerFanclubStatus);

    default:
      return state;
  }
}

export const NAME = 'profile';

// input Selectors
const broadcastSource = (state) => state[NAME].get('broadcastSource');
const fanClubProductId = (state) => state[NAME].get('fanClubProductId');
const hasFanClub = (state) => state[NAME].get('hasFanClub');
const isFan = (state) => state[NAME].get('isFan');
const performerDetailedInfo = (state) => state[NAME].get('performerDetailedInfo');
const performerFanclubStatus = (state) => state[NAME].get('performerFanclubStatus');
const performerFavouritesInfo = (state) => state[NAME].get('performerFavourites');
const performerFriendsStatus = (state) => state[NAME].get('performerFriendsStatus');
const performerGalleries = (state) => state[NAME].get('performerGalleries');
const performerGifts = (state) => state[NAME].get('performerGifts');
const performerInfo = (state) => state[NAME].get('performerInfo');
const performerVideos = (state) => state[NAME].get('performerVideos');
const performerWishlist = (state) => state[NAME].get('performerWishlist');
const playToken = (state) => state[NAME].get('playToken');
const processorId = (state) => state[NAME].get('processorId');
const serverTime = (state) => state[NAME].get('serverTime');
const tokenPrice = (state) => state[NAME].get('tokenPrice');

// ACTIONS
const fetchBroadcastSource = makeAction(GET_STREAM_URL, 'broadcastSource');
const fetchPerformerDetailedInfo = makeAction(GET_PERFORMER_DETAILED_INFO, 'performerDetailedInfo');
const fetchPerformerFriendsStatus = makeAction(GET_PERFORMER_FRIENDS_STATUS, 'performerFriendsStatus');
const fetchPerformerGalleries = makeAction(GET_PERFORMER_GALLERIES, 'performerGalleries');
const fetchPerformerGifts = makeAction(GET_PERFORMER_GIFTS, 'performerGifts');
const fetchPerformerInfo = makeAction(GET_PERFORMER_INFO, 'performerInfo');
const fetchPerformerVideos = makeAction(GET_PERFORMER_VIDEOS, 'performerVideos');
const fetchPerformerWishlist = makeAction(GET_PERFORMER_WISHLIST, 'performerWishlist');
const fetchServerTime = makeAction(GET_SERVER_TIME, 'serverTime');
const parsePlayToken = makeAction(SET_PLAY_TOKEN, 'playToken');
const setFanClubTokenPrice = makeAction(SET_FAN_CLUB_TOKEN_PRICE, 'tokenPrice');
const setFavouritesCount = makeAction(SET_PERFORMER_FAVOURITES_COUNT, 'favoritesCount');
const setFavouritesStatus = makeAction(SET_PERFORMER_FAVOURITES_STATUS, 'favoritesStatus');
const setHasFanClub = makeAction(SET_HAS_FAN_CLUB, 'hasFanClub');
const setIsFan = makeAction(SET_IS_FAN, 'isFan');
const setPerformerFanclubStatus = makeAction(SET_PERFORMER_FANCLUB_STATUS, 'performerFanclubStatus');

// API CALLS
function getServerTime() {
  const headers = { 'content-type': 'application/text' };

  return (dispatch) => ApiService.get(GET_SERVER_TIME_URL, { headers })
    .then((res) => {
      dispatch(fetchServerTime(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerInfo(username) {
  const url = ApiService.substitute(GET_PERFORMER_INFO_URL, { username });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(fetchPerformerInfo(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerDetailedInfo(username) {
  const url = ApiService.substitute(
    GET_PERFORMER_DETAILED_INFO_URL,
    { username: encodeURIComponent(username) }
  );

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(fetchPerformerDetailedInfo(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerVideos(username) {
  const url = ApiService.substitute(
    GET_PERFORMER_VIDEOS_URL,
    { username: encodeURIComponent(username) }
  );

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      if (res.status === StatusCodes.OK) {
        dispatch(fetchPerformerVideos(res.data));
      }
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerFanclubStatus(user) {
  const url = ApiService.substitute(FANCLUB_STATUS, { username: encodeURIComponent(user) });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      if (res.status === StatusCodes.OK) {
        dispatch(setPerformerFanclubStatus(res.data));
      }

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerGalleries(user) {
  const url = ApiService.substitute(GET_PERFORMER_GALLERIES_URL, { username: encodeURIComponent(user) });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(fetchPerformerGalleries(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerGalleryImages(username, galleryId) {
  const url = ApiService.substitute(GET_PERFORMER_GALLERY_IMAGES_URL, {
    username: encodeURIComponent(username),
    galleryId: encodeURIComponent(galleryId),
  });

  return () => ApiService.get(url)
    .then((res) => res)
    .catch((error) => {
      parseCatch(error);
    });
}

function getPerformerGifts(username) {
  const url = ApiService.substitute(GET_PERFORMER_GIFTS_URL, { username });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(fetchPerformerGifts(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerWishlist(username) {
  const url = ApiService.substitute(GET_PERFORMER_WISHLIST_URL, { username });

  return (dispatch) => ApiService.get(encodeURI(url))
    .then((res) => {
      dispatch(fetchPerformerWishlist(res.data));

      return res;
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getPerformerFriendsStatus(username, friendUsername, action) {
  if (!username) {
    return (dispatch) => dispatch(fetchPerformerFriendsStatus({ status: 'NONE' }));
  }

  const url = ApiService.substitute(FRIENDS_STATUS_URL, {
    username,
    friendUsername,
  });

  const getActionData = (actionName, actionType, actionResponse) => {
    const method = {
      getFriendsStatus: { request: ApiService.get },
      addFriend: {
        request: ApiService.post,
        state: { status: 'PENDING' },
      },
      removeFriend: {
        request: ApiService.delete,
        state: { status: 'NONE' },
      },
    };

    return method[actionName].request === ApiService.get ? actionResponse : method[actionName][actionType];
  };

  const boundMethod = getActionData(action, 'request').bind(ApiService);

  return (dispatch) => boundMethod(encodeURI(url))
    .then((res) => {
      dispatch(fetchPerformerFriendsStatus(getActionData(action, 'state', res.data)));

      return res;
    })
    .catch((error) => error.response);
}

function getPerformerFanClubInfo(username) {
  const url = ApiService.substitute(GET_FANCLUB_INFO_URL, { username });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(setHasFanClub(true));
      dispatch(setFanClubTokenPrice(res.data.tokenPrice));

      return res;
    })
    .catch((error) => {
      dispatch(setHasFanClub(false));

      return error.response;
    });
}

function getPerformerFanClubAuth(username = {}) {
  const url = ApiService.substitute(GET_FANCLUB_AUTHORIZATION_URL, { username });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(setIsFan(true));

      return res;
    })
    .catch((error) => {
      dispatch(setIsFan(false));

      return error.response;
    });
}

function getBroadcastSource(username) {
  const url = ApiService.substitute(GET_BROADCAST_SOURCE_URL, { username });

  return (dispatch) => ApiService.get(url)
    .then((res) => {
      dispatch(fetchBroadcastSource(res.data));

      return res;
    })
    .catch((error) => error.response);
}

function getPerformerFavouritesCount(dispatch, performerName) {
  const url = encodeURI(FAVOURITES_COUNT(performerName));

  ApiService.get(url, { withCredentials: false })
    .then((res) => {
      dispatch(setFavouritesCount(res.data.favoritedCount));
    })
    .catch((error) => {
      logger.error(error);
    });
}

function getPerformerFavouritesStatus(dispatch, user, performerName) {
  const { username, accessHash } = user;

  // When not logged
  if (!username) {
    return;
  }

  const url = encodeURI(FAVOURITES_BASE(username, performerName));
  const params = { accessHash };

  ApiService.get(url, { params })
    .then((res) => {
      dispatch(setFavouritesStatus(res.data.status));
    })
    .catch((error) => {
      logger.error(error);
    });
}

function getFavouritesInfo(user, performerName) {
  return (dispatch) => {
    getPerformerFavouritesCount(dispatch, performerName);
    getPerformerFavouritesStatus(dispatch, user, performerName);
  };
}

function toggleFavourite(status, user, performerName) {
  const { username, accessHash } = user;

  // When not logged
  if (!username) {
    return () => undefined;
  }

  const url = encodeURI(FAVOURITES_BASE(username, performerName));
  const method = status === ENUM_FAVOURITE_STATUS.NOT_FAVORITE ? ApiService.post : ApiService.delete;
  const boundMethod = method.bind(ApiService);
  const params = { accessHash };

  return (dispatch) => boundMethod(url, { params })
    .then((res) => {
      if (res.status === StatusCodes.OK) {
        dispatch(getFavouritesInfo(user, performerName));
      }
    })
    .catch((error) => {
      logger.error(error);
    });
}

function setPlayToken(json) {
  const { username } = json;
  const url = ApiService.substitute(PRIVATE_SHOW_ACTION_URL, { username });
  const data = qs.stringify(json);
  const headers = { 'content-type': 'application/x-www-form-urlencoded' };

  return (dispatch) => ApiService.post(url, { data, headers })
    .then((res) => {
      dispatch(parsePlayToken(res.data.playToken));
    })
    .catch((error) => {
      logger.error(error);

      return error.response;
    });
}

function getFanClubProduct(user, performer, amount) {
  const { username, accessHash } = user;
  const url = ApiService.substitute(
    FAN_CLUB_PRODUCT_URL,
    { username, accessHash, performer, amount }
  );

  return ApiService.get(url, { withCredentials: false });
}

function subscribeToFanClub(user, performerName, tokenPrice) {
  return async() => {
    const { username, accessHash } = user;

    try {
      const res = await getFanClubProduct(user, performerName, tokenPrice);
      if (isValidResponse(res)) {
        const url = ApiService.substitute(PAY_SUBSCRIPTION_URL, {
          username,
          productId: res.data.products[0].id,
          processorId: res.data.products[0].categories[0].methods[0].processorId,
        });
        const data = {
          userName: username,
          accessHash,
          target: performerName,
          amount: parseInt(tokenPrice, 10),
          originalDomain: getDomain(),
          returnPath: routeDefinitions.checkout,
          device: isMobile ? 'Mobile' : 'Desktop',
        };

        return ApiService.post(encodeURI(url), { data, withCredentials: false });
      }
      logger.warn(getApiWarnMsg('subscribeToFanClub', res.data));

      return res.data;

    } catch (error) {
      logger.error(error);

      return error;
    }
  };
}

function setAsFan(flag) {
  return (dispatch) => {
    dispatch(setIsFan(flag));
  };
}

export const selector = createStructuredSelector({
  broadcastSource,
  serverTime,
  performerInfo,
  performerDetailedInfo,
  performerVideos,
  performerGalleries,
  performerGifts,
  performerFriendsStatus,
  performerFanclubStatus,
  performerFavouritesInfo,
  playToken,
  isFan,
  hasFanClub,
  tokenPrice,
  fanClubProductId,
  processorId,
  performerWishlist,
});

export const actionCreators = {
  getServerTime,
  getPerformerFriendsStatus,
  getPerformerInfo,
  getPerformerVideos,
  getPerformerDetailedInfo,
  getPerformerFanClubInfo,
  getPerformerFanClubAuth,
  getPerformerFanclubStatus,
  getPerformerGalleries,
  getPerformerGalleryImages,
  getPerformerGifts,
  getBroadcastSource,
  getFavouritesInfo,
  getFanClubProduct,
  toggleFavourite,
  setPlayToken,
  setAsFan,
  subscribeToFanClub,
  getPerformerWishlist,
  parsePlayToken,
};
